import { DATACENTER } from '../actionTypes'

// ========================= BasicInfo =========================

type BikeInfo = {
  percent: number | null
  percentName: string | null
  registerCount: number | null
  registerCountName: string | null
  totalCount: number | null
  totalCountName: string | null
  unRegisterCount: number | null
  unRegisterCountName: string | null
}

type OperationInfo = {
  operationCarCount: number | null
  operatorCount: number | null
}

type BikeBasicInfo = {
  bikeInfo?: BikeInfo
  operationInfo?: OperationInfo
  scanRecordInfo?: {
    label: string
    value: string
    companyCode?: number
  }[]
  scanRecordInfoName: string
}

type SpockBasicInfo = BikeBasicInfo
type BasicInfo = [BikeBasicInfo, SpockBasicInfo]

// ========================= MapInfo =========================
type BikeMapInfo = {
  mapAreaData?: {
    [areaCode: number]: {
      activeCarCount: number
      hasEntity: boolean
      operationCarCount: number
      orderCount: number
      rideDistanceCount: number
    }
  }
  todayDataTotal?: {
    activeCarCount: number
    activeCarCountName: string
    operationCarCount: number
    operationCarCountName: string
    orderCount: number
    orderCountName: null
    rideDistanceCount: number
    rideDistanceCountName: string
    illegalParkingCount: number
    illegalParkingName: string
    applyBikeCount: number
  }
}
type SpockMapInfo = BikeMapInfo
type MapInfo = [BikeMapInfo, SpockMapInfo]

// ========================= StatisticInfo =========================
type BikeStatisticInfo = {
  statisticInfo?: {
    avgRideDistanceCount: number
    avgRideTime: number
    carbonReductionCount: number
    heatConsumptionCount: number
    replaceDriving: number
    totalDistance: number
    treePlanting: number
  }
}

type SpockStatisticInfo = BikeStatisticInfo
type AllStatisticInfo = BikeStatisticInfo
type StatisticInfo = [BikeStatisticInfo, SpockStatisticInfo, AllStatisticInfo]

// ========================= OrderBikeTrendInfo =========================
type BikeOrderBikeTrendInfo = {
  orderCarTrend?: {
    activeCarTrend?: {
      [key: number | string]: number | undefined
    }
    activeCarTrendName: string
    orderCountTrend?: {
      [key: number | string]: number | undefined
    }
    orderCountTrendName: string
  }
}
type SpockOrderBikeTrendInfo = BikeOrderBikeTrendInfo
type OrderBikeTrendInfo = [BikeOrderBikeTrendInfo, SpockOrderBikeTrendInfo]

// ========================= ParkingPointAlarmList =========================
type ParkingPointAlarmList = {
  alarmTime: string
  parkingPointId: string
  parkingPointName: string
  siltingUpDegree: string
  siltingUpDegreeName: string
}[]

// ========================= OrderDetail =========================
type OrderDetail = {
  street: string
  recordId: string
  updateTime: string
  statusName: string
  status: ComplaintStatus
  businessType: BusinessType
  completePic: string[] | null
}

// ========================= WordOrderDetail =========================
type WordOrderDetail = {
  finishTime: string
  statusName: string
  orderAddress: string
  finishPhotos: string[] | null
}

// ========================= AreaInfo =========================

type AreaInfo = {
  center: LngLat
  region: LngLat[][][]
}

// ========================= AllMapBikeInfo =========================
type MapBikeInfo = {
  batteryVoltage: number
  bikeId: string
  bikeReportLngLat: LngLat
  lat: number
  lng: number
  useIot: boolean
  bikeStatus: BikeStatus
  bikeType: BusinessType
  cardStatus: any
  frameId: any
  licenseCode: any
}[]

type MapSpockInfo = MapBikeInfo

type AllMapBikeInfo = [MapBikeInfo, MapSpockInfo]

// ========================= ParkingCoordinates =========================

type BikeParkingCoordinates = {
  center: LngLat
  businessType: BusinessType
  parkingPointId: string
  parkingPointName: string
  region: LngLat[][][] | null
  parkingType: ParkingType
}[]

type SpockParkingCoordinates = BikeParkingCoordinates

type ParkingCoordinates = [BikeParkingCoordinates, SpockParkingCoordinates]

// ========================= CityInfo =========================

type CityInfo = {
  adCode: string
  bikeCount: number
  lat: number
  lng: number
  name: string
}[]

// ========================= OperationPosition =========================

type OperatorPosition = {
  carrierNum: '电动车'
  molaOpsLag: [123.38064545045845, 41.79375499127221]
  sysUserId: 233401
  userName: null
  userPhone: null
}

type OperationPosition = {
  operatorPosition: OperatorPosition[]
  operatorCarPosition: OperatorPosition[]
}

// ========================= HeatMap =========================
type BikeHeatMap = {
  batteryVoltage: number
  bikeId: string
  bikeReportLngLat: LngLat
  lat: number
  lng: number
  useIot: boolean
  bikeStatus: BikeStatus
  bikeType: BusinessType
  cardStatus: any
  frameId: any
  licenseCode: any
}[]

type SpockHeatMap = BikeHeatMap

type HeatMap = { bikeLocations: BikeHeatMap; spockLocations: SpockHeatMap }

// ========================= BikeLatestOrder =========================
type BikeLatestOrder = {
  bikeType: BusinessType
  uniqueId: string
  orderId: string
  startTime: string
  endTime: string
  totalDuration: number
  totalFee: number
  actual_revenue: number
  // 1:正常停放2.禁停区外还车3. 停车点外还车4.运营区外还车5.未规范还车
  returnStatus: 1 | 2 | 3 | 4 | 5
  returnStatusDesc: string
}

// ========================= DataCenterState =========================
interface DataCenterState {
  basicInfo: BasicInfo
  statisticInfo: StatisticInfo
  mapInfo: MapInfo
  orderBikeTrendInfo: OrderBikeTrendInfo
  areaInfo: AreaInfo
  parkingPointAlarmList: ParkingPointAlarmList
  scrollOrder: OrderDetail[]
  finishOrder: OrderDetail[]
  fullScreen: boolean | undefined
  reserveWorkOrder: WordOrderDetail[]
  feedBackWorkOrder: WordOrderDetail[]
  cityInfo: CityInfo
  bikeInfo: AllMapBikeInfo
  parkingCoordinates: ParkingCoordinates
  operationPosition: OperationPosition
  heatMap: HeatMap
  showBikeOrderList: boolean
  bikeLatestOrderList: BikeLatestOrder[]
}

const initialState: DataCenterState = {
  basicInfo: [{}, {}] as BasicInfo,
  statisticInfo: [{}, {}, {}] as StatisticInfo,
  mapInfo: [{}, {}],
  orderBikeTrendInfo: [{}, {}],
  areaInfo: {} as AreaInfo,
  parkingPointAlarmList: [],
  scrollOrder: [],
  finishOrder: [],
  reserveWorkOrder: [],
  feedBackWorkOrder: [],
  fullScreen: undefined,
  cityInfo: [],
  bikeInfo: [{}, {}] as AllMapBikeInfo,
  parkingCoordinates: [{}, {}] as ParkingCoordinates,
  operationPosition: {} as OperationPosition,
  heatMap: {} as HeatMap,
  showBikeOrderList: false,
  bikeLatestOrderList: [],
}

export default function response(state = initialState, action: Action) {
  switch (action.type) {
    case DATACENTER.UPDATE_STATE:
      return { ...state, ...action.payload }
    default:
      return state
  }
}
